import { useId, useState, type FormEvent, type ReactNode } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';

import { CohortFields } from './CohortFields';
import {
  DELIVERY_KIND_DESCRIPTIONS,
  DELIVERY_KIND_LABELS,
  DELIVERY_KINDS,
  ROUTINE_AGENT_MODES,
  ROUTINE_KINDS,
  ROUTINE_KIND_HELP,
} from './deliveryKinds';
import {
  composeRecurrenceRule,
  DEFAULT_RECURRENCE_SELECTION,
  parseRecurrenceRule,
  RECURRENCE_FREQUENCIES,
  WEEKDAY_CODES,
} from './recurrenceRule';
import { targetsSummary } from './targetsSummary';
import { useCreateProactivityExperiment } from './useProactivityExperiments';

import type {
  CreateProactivityExperimentInput,
  ProactivityExperimentDeliveryKind,
  ProactivityExperimentDetail,
  ProactivityExperimentOptions,
  ProactivityExperimentRoutineAgentMode,
  ProactivityExperimentRoutineKind,
} from './proactivity-experiments-types';
import type {
  RecurrenceFrequency,
  RecurrenceSelection,
  WeekdayCode,
} from './recurrenceRule';

const CREATE_FORM_PLACEHOLDERS = {
  name: 'Workflow Analysis',
  card_title: "What's your workflow?",
  card_body:
    'Littlebird analyses your usage patterns and creates a detailed report about your workflow',
  prompt:
    'Analyze my recent activity and create a detailed report on my workflow, habits, tools, and opportunities to improve.',
  routine_title: 'Top of mind',
  routine_recurrence_rule: 'FREQ=DAILY;BYHOUR=9;BYMINUTE=0',
} as const;

type ProactivityExperimentDialogProps = {
  options: ProactivityExperimentOptions;
  mode: 'create' | 'duplicate';
  source?: Pick<
    ProactivityExperimentDetail,
    | 'name'
    | 'delivery_kind'
    | 'visible_to_user'
    | 'billed_to_user'
    | 'enroll_new_signups'
    | 'card_title'
    | 'card_body'
    | 'prompt'
    | 'routine_title'
    | 'routine_recurrence_rule'
    | 'routine_kind'
    | 'routine_agent_mode'
    | 'execution'
    | 'cohort'
  >;
  onOpenChange: (open: boolean) => void;
};

const DIALOG_COPY = {
  create: {
    title: 'Create proactivity experiment',
    description: 'Issue a card, a chat, or a routine to a PostHog cohort.',
    submitLabel: 'Create experiment',
    pendingLabel: 'Creating...',
  },
  duplicate: {
    title: 'Duplicate proactivity experiment',
    description: 'Review the copy, then create a new experiment.',
    submitLabel: 'Create experiment',
    pendingLabel: 'Creating...',
  },
} as const;

const isDeliveryKind = (
  value: string
): value is ProactivityExperimentDeliveryKind =>
  (DELIVERY_KINDS as readonly string[]).includes(value);

const isRoutineKind = (
  value: string
): value is ProactivityExperimentRoutineKind =>
  (ROUTINE_KINDS as readonly string[]).includes(value);

const RECURRENCE_FREQUENCY_LABELS: Record<RecurrenceFrequency, string> = {
  daily: 'Every day',
  weekdays: 'Every weekday',
  weekly: 'Chosen days',
  hourly: 'Every hour',
};

const WEEKDAY_LABELS: Record<WeekdayCode, string> = {
  MO: 'Mon',
  TU: 'Tue',
  WE: 'Wed',
  TH: 'Thu',
  FR: 'Fri',
  SA: 'Sat',
  SU: 'Sun',
};

const isRecurrenceFrequency = (value: string): value is RecurrenceFrequency =>
  (RECURRENCE_FREQUENCIES as readonly string[]).includes(value);

const isRoutineAgentMode = (
  value: string
): value is ProactivityExperimentRoutineAgentMode =>
  (ROUTINE_AGENT_MODES as readonly string[]).includes(value);

const CheckboxField = ({
  name,
  label,
  help,
  checked,
  onChange,
}: {
  name: string;
  label: string;
  help: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) => (
  <label className="flex items-start gap-2 text-sm">
    <input
      type="checkbox"
      name={name}
      className="mt-0.5"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span className="grid gap-0.5">
      <span>{label}</span>
      <span className="text-xs text-muted-foreground">{help}</span>
    </span>
  </label>
);

const RecurrenceField = ({
  sourceRule,
  placeholder,
}: {
  sourceRule: string | undefined;
  placeholder: string | undefined;
}) => {
  const recurrenceRuleId = useId();
  // A source rule the dropdowns cannot express opens the field in Advanced
  const sourceSelection = sourceRule?.trim()
    ? parseRecurrenceRule(sourceRule)
    : DEFAULT_RECURRENCE_SELECTION;
  const [pickedSelection, setPickedSelection] = useState<RecurrenceSelection>(
    sourceSelection ?? DEFAULT_RECURRENCE_SELECTION
  );
  const [rawRule, setRawRule] = useState(sourceRule ?? '');
  const [advanced, setAdvanced] = useState(sourceSelection === null);
  const rule = advanced ? rawRule : composeRecurrenceRule(pickedSelection);
  // The raw box is authoritative while Advanced is on, so the dropdowns follow it where they can
  const selection =
    (advanced ? parseRecurrenceRule(rawRule) : null) ?? pickedSelection;

  const applySelection = (change: Partial<RecurrenceSelection>) => {
    const next = { ...selection, ...change };
    setPickedSelection(next);
    if (advanced) setRawRule(composeRecurrenceRule(next));
  };

  const toggleWeekday = (code: WeekdayCode) => {
    const weekdays = selection.weekdays.includes(code)
      ? selection.weekdays.filter((day) => day !== code)
      : [...selection.weekdays, code];
    if (weekdays.length > 0) applySelection({ weekdays });
  };

  const toggleAdvanced = (on: boolean) => {
    if (on) setRawRule(rule);
    else setPickedSelection(selection);
    setAdvanced(on);
  };

  const timeValue = `${String(selection.hour).padStart(2, '0')}:${String(selection.minute).padStart(2, '0')}`;

  return (
    <>
      <div className="grid gap-4 sm:grid-cols-2">
        <label className="grid gap-1.5 text-sm">
          Frequency
          <Select
            value={selection.frequency}
            onChange={(event) => {
              if (isRecurrenceFrequency(event.target.value)) {
                applySelection({ frequency: event.target.value });
              }
            }}
          >
            {RECURRENCE_FREQUENCIES.map((frequency) => (
              <option key={frequency} value={frequency}>
                {RECURRENCE_FREQUENCY_LABELS[frequency]}
              </option>
            ))}
          </Select>
        </label>
        {selection.frequency !== 'hourly' ? (
          <label className="grid gap-1.5 text-sm">
            Time
            <Input
              type="time"
              value={timeValue}
              onChange={(event) => {
                const [hour, minute] = event.target.value.split(':');
                if (minute === undefined) return;
                applySelection({ hour: Number(hour), minute: Number(minute) });
              }}
            />
          </label>
        ) : null}
      </div>
      {selection.frequency === 'weekly' ? (
        <fieldset className="grid gap-1.5 text-sm">
          <legend className="mb-1.5">Days</legend>
          <div className="flex flex-wrap gap-3">
            {WEEKDAY_CODES.map((code) => (
              <label key={code} className="flex items-center gap-1.5">
                <input
                  type="checkbox"
                  checked={selection.weekdays.includes(code)}
                  onChange={() => toggleWeekday(code)}
                />
                {WEEKDAY_LABELS[code]}
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}
      {advanced ? null : (
        <>
          <span className="text-xs text-muted-foreground">
            Runs on {rule}, anchored in each recipient's timezone.
          </span>
          <input type="hidden" name="routine_recurrence_rule" value={rule} />
        </>
      )}
      <CheckboxField
        name="recurrence_advanced"
        label="Advanced"
        help="Write the RRULE body by hand, for schedules the dropdowns cannot express."
        checked={advanced}
        onChange={toggleAdvanced}
      />
      {advanced ? (
        <div className="grid gap-1.5 text-sm">
          <label htmlFor={recurrenceRuleId}>Recurrence rule</label>
          <Input
            id={recurrenceRuleId}
            name="routine_recurrence_rule"
            required
            maxLength={255}
            value={rawRule}
            onChange={(event) => setRawRule(event.target.value)}
            placeholder={placeholder}
          />
          <span className="text-xs text-muted-foreground">
            An RRULE body such as FREQ=DAILY;BYHOUR=9;BYMINUTE=0, anchored in
            each recipient's timezone.
          </span>
        </div>
      ) : null}
    </>
  );
};

const RoutineFields = ({
  source,
  placeholders,
  routineKind,
  onRoutineKindChange,
  routineAgentMode,
  onRoutineAgentModeChange,
}: {
  source: ProactivityExperimentDialogProps['source'];
  placeholders: typeof CREATE_FORM_PLACEHOLDERS | null;
  routineKind: ProactivityExperimentRoutineKind;
  onRoutineKindChange: (kind: ProactivityExperimentRoutineKind) => void;
  routineAgentMode: ProactivityExperimentRoutineAgentMode;
  onRoutineAgentModeChange: (
    mode: ProactivityExperimentRoutineAgentMode
  ) => void;
}) => {
  const routineKindId = useId();
  return (
    <>
      <label className="grid gap-1.5 text-sm">
        Routine title
        <Input
          name="routine_title"
          required
          maxLength={120}
          defaultValue={source?.routine_title}
          placeholder={placeholders?.routine_title}
        />
      </label>
      <RecurrenceField
        sourceRule={source?.routine_recurrence_rule}
        placeholder={placeholders?.routine_recurrence_rule}
      />
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="grid gap-1.5 text-sm">
          <label htmlFor={routineKindId}>Routine kind</label>
          <Select
            id={routineKindId}
            name="routine_kind"
            value={routineKind}
            onChange={(event) => {
              if (isRoutineKind(event.target.value)) {
                onRoutineKindChange(event.target.value);
              }
            }}
          >
            {ROUTINE_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {kind}
              </option>
            ))}
          </Select>
        </div>
        <label className="grid gap-1.5 text-sm">
          Agent mode
          <Select
            name="routine_agent_mode"
            value={routineAgentMode}
            onChange={(event) => {
              if (isRoutineAgentMode(event.target.value)) {
                onRoutineAgentModeChange(event.target.value);
              }
            }}
          >
            {ROUTINE_AGENT_MODES.map((agentMode) => (
              <option key={agentMode} value={agentMode}>
                {agentMode}
              </option>
            ))}
          </Select>
        </label>
        <span className="text-xs text-muted-foreground sm:col-span-2">
          {ROUTINE_KIND_HELP[routineKind]}
        </span>
      </div>
    </>
  );
};

const Notice = ({ children }: { children: ReactNode }) => (
  <p className="text-amber-700 dark:text-amber-400">{children}</p>
);

export const ProactivityExperimentDialog = ({
  options,
  mode,
  source,
  onOpenChange,
}: ProactivityExperimentDialogProps) => {
  const createExperiment = useCreateProactivityExperiment();
  const deliveryKindId = useId();
  const initialExecutionKind = source?.execution.kind ?? 'mode';
  const listExecutionOptions = (kind: 'mode' | 'agent') =>
    kind === 'mode' ? options.chat_modes : options.agents;
  const pickExecution = (kind: 'mode' | 'agent') => {
    const executionOptions = listExecutionOptions(kind);
    if (mode === 'create') return executionOptions[0] ?? '';
    if (
      source?.execution.kind === kind &&
      executionOptions.includes(source.execution.value)
    ) {
      return source.execution.value;
    }
    return '';
  };
  const [deliveryKind, setDeliveryKind] =
    useState<ProactivityExperimentDeliveryKind>(
      source?.delivery_kind ?? 'card_prompt'
    );
  const [visibleToUser, setVisibleToUser] = useState(
    source?.visible_to_user ?? true
  );
  const [billedToUser, setBilledToUser] = useState(
    source?.billed_to_user ?? true
  );
  const [enrollNewSignups, setEnrollNewSignups] = useState(
    source?.enroll_new_signups ?? false
  );
  const [routineKind, setRoutineKind] =
    useState<ProactivityExperimentRoutineKind>(
      source?.routine_kind ?? 'digest'
    );
  const [routineAgentMode, setRoutineAgentMode] =
    useState<ProactivityExperimentRoutineAgentMode>(
      source?.routine_agent_mode ?? 'regular'
    );
  const [executionKind, setExecutionKind] = useState(initialExecutionKind);
  const [executionValue, setExecutionValue] = useState(() =>
    pickExecution(initialExecutionKind)
  );
  const [cohortValue, setCohortValue] = useState(() =>
    source?.cohort ? String(source.cohort.id) : ''
  );
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const copy = DIALOG_COPY[mode];
  const selectedCohort = options.cohorts.find(
    (cohort) => String(cohort.id) === cohortValue
  );
  const sourceCohort = source?.cohort ?? null;
  const staleSourceCohort =
    mode === 'duplicate' &&
    sourceCohort !== null &&
    !options.cohorts.some((cohort) => cohort.id === sourceCohort.id);
  const hasRecipients =
    selectedCohort !== undefined || additionalEmails.length > 0;
  const executionOptions = listExecutionOptions(executionKind);
  const executionReady =
    executionValue !== '' && executionOptions.includes(executionValue);
  const staleSourceExecution =
    mode === 'duplicate' &&
    source?.execution.kind === executionKind &&
    !executionOptions.includes(source.execution.value);
  const isCardPrompt = deliveryKind === 'card_prompt';
  const isDirectRoutine = deliveryKind === 'direct_routine';

  const handleExecutionKindChange = (kind: 'mode' | 'agent') => {
    setExecutionKind(kind);
    setExecutionValue(pickExecution(kind));
  };

  // Mirrors the backend check: card prompts need a card title, direct routines a title and a rule
  const validate = (
    form: FormData
  ): Pick<
    CreateProactivityExperimentInput,
    | 'name'
    | 'prompt'
    | 'card_title'
    | 'routine_title'
    | 'routine_recurrence_rule'
  > | null => {
    const name = String(form.get('name')).trim();
    const prompt = String(form.get('prompt')).trim();
    if (!name || !prompt) {
      setValidationError('Name and prompt cannot be blank.');
      return null;
    }
    const cardTitle = isCardPrompt
      ? String(form.get('card_title')).trim()
      : null;
    if (isCardPrompt && !cardTitle) {
      setValidationError('A card prompt needs a card title.');
      return null;
    }
    const routineTitle = isDirectRoutine
      ? String(form.get('routine_title')).trim()
      : null;
    const routineRecurrenceRule = isDirectRoutine
      ? String(form.get('routine_recurrence_rule')).trim()
      : null;
    if (isDirectRoutine && (!routineTitle || !routineRecurrenceRule)) {
      setValidationError(
        'A direct routine needs a routine title and a recurrence rule.'
      );
      return null;
    }
    if (!hasRecipients) {
      setValidationError('Pick a cohort or add at least one email.');
      return null;
    }
    return {
      name,
      prompt,
      card_title: cardTitle,
      routine_title: routineTitle,
      routine_recurrence_rule: routineRecurrenceRule,
    };
  };

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const validated = validate(form);
    if (!validated) return;
    setValidationError(null);
    const input: CreateProactivityExperimentInput = {
      ...validated,
      delivery_kind: deliveryKind,
      // A card is always shown and its chat is the user's own, so the toggles only apply to chats and routines
      visible_to_user: isCardPrompt || visibleToUser,
      billed_to_user: isCardPrompt || billedToUser,
      enroll_new_signups: !isCardPrompt && enrollNewSignups,
      card_body: isCardPrompt ? String(form.get('card_body')) : '',
      execution:
        executionKind === 'mode'
          ? { kind: 'mode', value: executionValue }
          : { kind: 'agent', value: executionValue },
      routine_kind: isDirectRoutine ? routineKind : null,
      routine_agent_mode: isDirectRoutine ? routineAgentMode : null,
      cohort_id: selectedCohort?.id ?? null,
      additional_emails: additionalEmails,
    };
    createExperiment.mutate(input, {
      onSuccess: () => onOpenChange(false),
    });
  };

  const fieldPlaceholders = mode === 'create' ? CREATE_FORM_PLACEHOLDERS : null;

  const handleOpenChange = (open: boolean) => {
    if (createExperiment.isPending) return;
    onOpenChange(open);
  };

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-h-[90vh] max-w-2xl overflow-y-auto"
        onEscapeKeyDown={(event) => {
          if (createExperiment.isPending) event.preventDefault();
        }}
        onPointerDownOutside={(event) => {
          if (createExperiment.isPending) event.preventDefault();
        }}
      >
        <form className="space-y-5" onSubmit={handleSubmit}>
          <DialogHeader className="space-y-1.5">
            <DialogTitle>{copy.title}</DialogTitle>
            <DialogDescription>{copy.description}</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <label className="grid gap-1.5 text-sm">
              Name
              <Input
                name="name"
                required
                maxLength={120}
                defaultValue={source?.name}
                placeholder={fieldPlaceholders?.name}
              />
            </label>
            <div className="grid gap-1.5 text-sm">
              <label htmlFor={deliveryKindId}>Delivery</label>
              <Select
                id={deliveryKindId}
                name="delivery_kind"
                value={deliveryKind}
                onChange={(event) => {
                  if (isDeliveryKind(event.target.value)) {
                    setDeliveryKind(event.target.value);
                  }
                }}
              >
                {DELIVERY_KINDS.map((kind) => (
                  <option key={kind} value={kind}>
                    {DELIVERY_KIND_LABELS[kind]}
                  </option>
                ))}
              </Select>
              <span className="text-xs text-muted-foreground">
                {DELIVERY_KIND_DESCRIPTIONS[deliveryKind]}
              </span>
            </div>
            {isCardPrompt ? (
              <>
                <label className="grid gap-1.5 text-sm">
                  Card title
                  <Input
                    name="card_title"
                    required
                    maxLength={120}
                    defaultValue={source?.card_title}
                    placeholder={fieldPlaceholders?.card_title}
                  />
                </label>
                <label className="grid gap-1.5 text-sm">
                  Card body
                  <Textarea
                    name="card_body"
                    maxLength={500}
                    defaultValue={source?.card_body}
                    placeholder={fieldPlaceholders?.card_body}
                  />
                </label>
              </>
            ) : null}
            {isDirectRoutine ? (
              <RoutineFields
                source={source}
                placeholders={fieldPlaceholders}
                routineKind={routineKind}
                onRoutineKindChange={setRoutineKind}
                routineAgentMode={routineAgentMode}
                onRoutineAgentModeChange={setRoutineAgentMode}
              />
            ) : null}
            <label className="grid gap-1.5 text-sm">
              Prompt
              <Textarea
                name="prompt"
                required
                maxLength={80_000}
                rows={8}
                defaultValue={source?.prompt}
                placeholder={fieldPlaceholders?.prompt}
              />
            </label>
          </div>

          {!isCardPrompt && (
            <fieldset className="grid gap-3">
              <legend className="mb-2 text-sm font-medium">
                Visibility and billing
              </legend>
              <CheckboxField
                name="visible_to_user"
                label="Visible to user"
                help="The routine or chat shows in the user's lists and notifies them. Off hides it from everyone but internal users."
                checked={visibleToUser}
                onChange={setVisibleToUser}
              />
              <CheckboxField
                name="billed_to_user"
                label="Billed to user"
                help="Runs and turns debit the user's credits and stop when they run out. Off means Littlebird pays and nothing gates them."
                checked={billedToUser}
                onChange={setBilledToUser}
              />
              <CheckboxField
                name="enroll_new_signups"
                label="Enrol new signups"
                help="Every user who finishes onboarding while this is on joins the experiment, on top of the cohort."
                checked={enrollNewSignups}
                onChange={setEnrollNewSignups}
              />
            </fieldset>
          )}

          <div className="space-y-2">
            <CohortFields
              cohorts={options.cohorts}
              cohortValue={cohortValue}
              onCohortValueChange={setCohortValue}
              emails={additionalEmails}
              onEmailsChange={setAdditionalEmails}
            />

            {staleSourceCohort && sourceCohort ? (
              <p className="text-sm text-amber-700 dark:text-amber-400">
                Cohort {sourceCohort.name} is no longer available. Pick a
                replacement.
              </p>
            ) : null}
          </div>

          <fieldset className="space-y-2">
            <legend className="text-sm font-medium">Execution</legend>
            <div className="flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="execution_kind"
                  value="mode"
                  checked={executionKind === 'mode'}
                  onChange={() => handleExecutionKindChange('mode')}
                />
                Mode
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="execution_kind"
                  value="agent"
                  checked={executionKind === 'agent'}
                  onChange={() => handleExecutionKindChange('agent')}
                />
                Direct agent
              </label>
            </div>
            <Select
              name="execution_value"
              required
              value={executionValue}
              onChange={(event) => setExecutionValue(event.target.value)}
            >
              {executionOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </Select>
            {staleSourceExecution && source ? (
              <p className="text-sm text-amber-700 dark:text-amber-400">
                {source.execution.value} is no longer available. Pick a
                replacement.
              </p>
            ) : null}
          </fieldset>

          <div className="space-y-2 rounded-md border bg-muted/30 p-3 text-sm">
            <p>{targetsSummary(selectedCohort, additionalEmails.length)}</p>
            {isCardPrompt ? (
              <Notice>
                Prompt must support ask_user. Unsupported agents may fail after
                send.
              </Notice>
            ) : null}
            {!billedToUser ? (
              <Notice>
                Unbilled artifacts run at Littlebird's cost until paused or
                revoked.
              </Notice>
            ) : null}
          </div>

          {validationError || createExperiment.error ? (
            <p role="alert" className="text-sm text-destructive">
              {validationError ?? createExperiment.error?.message}
            </p>
          ) : null}

          <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                disabled={createExperiment.isPending}
              >
                Cancel
              </Button>
            </DialogClose>
            <Button
              type="submit"
              disabled={
                createExperiment.isPending || !executionReady || !hasRecipients
              }
            >
              {createExperiment.isPending
                ? copy.pendingLabel
                : copy.submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
